<script setup lang="ts">
// OnboardingBlock — the interactive "Step 1 of onboarding" block.
// A real 3-stage mini-flow the visitor completes before signing up:
//   о чём → вопрос → (мурчим…) → карта + тёплый ответ → продолжить
// In paper: a raised sheet taped onto the hero's sky; the intents are slips of their
// own coloured stock, the progress is three small paper tabs, the question goes into a
// recessed well, and the card is a real deck card (a sticker) the visitor turns over.
//
// SSG/hydration-safe: SSR renders stage 0 (intent grid); all state is plain
// refs at their pre-interaction initial values; the only timer/DOM access lives
// inside event handlers / watch and is cleaned up in onUnmounted.
import { nextTick, onUnmounted, ref, watch, type Component } from 'vue';
import { RouterLink } from 'vue-router';
import { BriefcaseBusiness, Heart, PawPrint, Split } from '@lucide/vue';
import { Button } from '@/components/ui/button';
import { useStatic } from '@core/services';
import TarotCard from '@app/presentation/components/tarot/TarotCard.vue';
import PaperCutout from '@app/presentation/components/paper/PaperCutout.vue';
import catIcon from '@app/assets/images/icons/profile_cat_icon.png';
import {
  APP_CTA_TO,
  APP_CTA_LABEL,
  TELEGRAM_BOT_URL,
} from '@app/presentation/components/site/constants';

interface OBIntent {
  key: string;
  icon: Component;
  label: string;
  hint: string;
  /** The intent's card stock (a `bg-stock-*` utility). */
  stock: string;
  placeholder: string;
  examples: string[];
  /** `deck` = the card face in the deck (public/cards/NN.webp). */
  card: { roman: string; name: string; deck: string };
  reading: string;
}

const OB_INTENTS: OBIntent[] = [
  {
    key: 'love', icon: Heart, label: 'Отношения', hint: 'что он чувствует',
    stock: 'bg-stock-rust',
    placeholder: 'Что он ко мне чувствует?',
    examples: ['Он напишет сегодня?', 'Почему он пропал?', 'У нас есть будущее?'],
    card: { roman: 'XVII', name: 'Звезда', deck: '17' },
    reading: 'Звезда говорит: тепло между вами не погасло — он просто сейчас в своих тревогах. Это про него, не про тебя. А ждать или нет — выбираешь только ты.',
  },
  {
    key: 'decision', icon: Split, label: 'Решение', hint: 'да или нет',
    stock: 'bg-stock-wine',
    placeholder: 'Стоит ли мне решиться?',
    examples: ['Сдам ли экзамен?', 'Увольняться?', 'Ехать или остаться?'],
    card: { roman: 'VII', name: 'Колесница', deck: '07' },
    reading: 'Колесница — это зелёный свет. Карты видят движение вперёд, если ты выберешь его сама. Сомнения сейчас нормальны, но путь уже открыт.',
  },
  {
    key: 'money', icon: BriefcaseBusiness, label: 'Деньги и работа', hint: 'где все деньги',
    stock: 'bg-stock-bronze',
    placeholder: 'Что у меня с деньгами?',
    examples: ['Найду ли работу?', 'Будет ли повышение?', 'Стоит ли рискнуть?'],
    card: { roman: 'A', name: 'Туз монет', deck: '36' },
    reading: 'Туз монет — про новое начало и почву под ногами. То, что ты уже обдумываешь, может стать опорой. Не спеши, но и не прячься от шанса.',
  },
  {
    key: 'talk', icon: PawPrint, label: 'Просто поговорить', hint: 'кот выслушает',
    stock: 'bg-stock-plum',
    placeholder: 'Поделись, что на душе…',
    examples: ['Мне тревожно', 'Я запуталась', 'Тяжёлый день'],
    card: { roman: 'XIX', name: 'Солнце', deck: '19' },
    reading: 'Иногда не нужны карты — нужно, чтобы кто-то выслушал. Я рядом. Расскажи, что случилось, и мы разложим это вместе, без спешки. Мур.',
  },
];

// Progress tab labels: 0 intent · 1 question · 2 answer
const PROGRESS_LABELS = ['О чём', 'Вопрос', 'Ответ'];
const SLIP_TILTS = [-0.6, 0.5, 0.4, -0.5];
const CHIP_TILTS = [-1.5, 1, -0.8];

const { getStaticUrl } = useStatic();
const cardBack = getStaticUrl('images/moon_magic_tarot/back.jpg');
const cardFace = (deck: string) => `${import.meta.env.BASE_URL}cards/${deck}.webp`;

const stage = ref(0); // 0 intent · 1 question · 2 answer
const intent = ref<OBIntent | null>(null);
const q = ref('');
const loading = ref(false);
// The first stage is in the static HTML: it only slides in when the visitor comes back to it.
const interacted = ref(false);
const flipped = ref(false);
const timer = ref<ReturnType<typeof setTimeout> | null>(null);

const inputEl = ref<HTMLInputElement | null>(null);
const cardRef = ref<InstanceType<typeof TarotCard> | null>(null);

const tabClass = (i: number) =>
  i < stage.value ? 'is-done paper-tag' : i === stage.value ? 'is-now paper-tag' : 'is-todo';

const pickIntent = (it: OBIntent) => {
  intent.value = it;
  q.value = '';
  interacted.value = true;
  stage.value = 1;
};

const ask = () => {
  stage.value = 2;
  loading.value = true;
  flipped.value = false;
  if (timer.value) clearTimeout(timer.value);
  timer.value = setTimeout(() => {
    loading.value = false;
  }, 1700);
};

const reset = () => {
  stage.value = 0;
  intent.value = null;
  q.value = '';
  loading.value = false;
  flipped.value = false;
};

// The card turns over once; after that it's just the card (aria-disabled keeps focus on it).
const onFlip = () => {
  if (flipped.value) return;
  cardRef.value?.flip();
  flipped.value = true;
};

// Autofocus the question input when stage 1 is shown (client-only, guarded).
watch(stage, (s) => {
  if (s === 1) {
    nextTick(() => {
      inputEl.value?.focus();
    });
  }
});

onUnmounted(() => {
  if (timer.value) clearTimeout(timer.value);
});
</script>

<template>
  <div class="ob-sheet paper-sheet-raised relative">
    <span
      class="ob-tape"
      aria-hidden="true"
    />

    <div class="relative flex min-h-[440px] flex-col px-5 pb-6 pt-7 sm:min-h-[488px] sm:px-7 sm:pt-8">
      <div class="mb-4 flex items-center justify-between">
        <span class="ob-step-tag paper-tag type-label inline-flex items-center gap-2 px-2.5 py-1">
          <span class="h-2 w-2 rounded-full bg-success" /> Шаг {{ stage + 1 }} из 3{{ stage === 0 ? ' · просто попробуй' : '' }}
        </span>
      </div>

      <!-- Progress: three small paper tabs — done cream · current cream, taller, lifted · todo a well. -->
      <div
        class="mb-[22px] flex h-3 items-center gap-1.5"
        aria-hidden="true"
      >
        <span
          v-for="(_, i) in PROGRESS_LABELS"
          :key="i"
          class="ob-tab flex-1"
          :class="tabClass(i)"
        />
      </div>

      <!-- Stage 0 · intent -->
      <div
        v-if="stage === 0"
        :class="['flex flex-1 flex-col', interacted && 'ob-step']"
      >
        <div class="type-heading text-foreground">Что у тебя на душе?</div>
        <div class="mt-2 text-[15px] leading-relaxed text-muted-foreground">
          Выбери, о чём думаешь — кот подберёт карты под твою ситуацию.
        </div>
        <div class="mt-[26px] grid grid-cols-1 gap-3 sm:grid-cols-2">
          <button
            v-for="(it, i) in OB_INTENTS"
            :key="it.key"
            type="button"
            class="ob-slip flex items-center gap-3 p-[13px] text-left text-foreground"
            :class="it.stock"
            :style="{ '--tilt': `${SLIP_TILTS[i]}deg` }"
            @click="pickIntent(it)"
          >
            <span
              class="ob-icon flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
              aria-hidden="true"
            >
              <component
                :is="it.icon"
                class="h-[18px] w-[18px]"
                :stroke-width="2"
              />
            </span>
            <span>
              <span class="type-body-strong block leading-snug">{{ it.label }}</span>
              <span class="mt-0.5 block text-[12px] leading-snug opacity-85">{{ it.hint }}</span>
            </span>
          </button>
        </div>
        <div class="type-caption mt-auto flex items-center justify-center gap-1.5 pt-[18px] text-center">
          <PawPrint
            class="h-3.5 w-3.5"
            aria-hidden="true"
          /> 2 000+ человек уже начали здесь
        </div>
      </div>

      <!-- Stage 1 · question -->
      <div
        v-else-if="stage === 1"
        class="ob-step flex flex-1 flex-col"
      >
        <div
          class="ob-kind type-label mb-3.5 inline-flex items-center gap-2 self-start px-2.5 py-1 text-foreground"
          :class="intent?.stock"
        >
          <component
            :is="intent?.icon"
            v-if="intent"
            class="h-3.5 w-3.5"
            aria-hidden="true"
          />
          {{ intent?.label }}
        </div>
        <div class="type-heading text-foreground">Спроси своими словами</div>
        <div class="mt-2 text-[15px] leading-relaxed text-muted-foreground">
          Как другу. Можно коротко — пары слов котику хватит.
        </div>
        <div class="ob-well mt-[22px] flex items-center gap-2.5 py-2 pl-[18px] pr-2">
          <input
            ref="inputEl"
            v-model="q"
            :placeholder="intent?.placeholder"
            aria-label="Твой вопрос"
            class="min-w-0 flex-1 border-none bg-transparent font-sans text-base text-foreground outline-none placeholder:text-muted-foreground"
            @keydown.enter="ask"
          />
          <!-- Phones: an arrow-only button, so the placeholder question isn't cut off mid-word. -->
          <Button
            size="lg"
            class="w-12 shrink-0 px-0 text-[15px] sm:w-auto sm:px-[18px]"
            @click="ask"
          >
            <span class="max-sm:sr-only">Спросить</span>
            <span aria-hidden="true">→</span>
          </Button>
        </div>
        <div class="mt-4 flex flex-wrap gap-2">
          <button
            v-for="(e, i) in intent?.examples"
            :key="i"
            type="button"
            class="ob-chip paper-tag px-[13px] py-[6px] font-sans text-[13px]"
            :style="{ '--tilt': `${CHIP_TILTS[i % CHIP_TILTS.length]}deg` }"
            @click="q = e"
          >
            {{ e }}
          </button>
        </div>
        <div class="mt-auto flex items-center gap-3 pt-[18px]">
          <button
            type="button"
            class="ob-link px-1 py-2 font-sans text-sm text-muted-foreground transition-colors hover:text-foreground"
            @click="stage = 0"
          >
            ← назад
          </button>
        </div>
      </div>

      <!-- Stage 2 · loading -->
      <div
        v-else-if="stage === 2 && loading"
        class="ob-step flex flex-1 flex-col"
        role="status"
      >
        <div class="flex flex-1 flex-col items-center justify-center gap-[18px]">
          <div aria-hidden="true">
            <PaperCutout
              name="wizard_cat"
              :width="150"
            />
          </div>
          <div class="type-subheading text-foreground">Мурчим твою судьбу<b class="ob-dots">…</b>🐾</div>
          <div class="text-sm text-muted-foreground">тасуем колоду и тянем карту</div>
        </div>
      </div>

      <!-- Stage 2 · reveal -->
      <div
        v-else-if="stage === 2 && !loading"
        class="ob-step flex flex-1 flex-col"
      >
        <div class="flex flex-1 flex-col items-center justify-center gap-4">
          <!-- The card: a real deck card as a sticker. A real button — Enter/Space turn it over. -->
          <div class="relative">
            <button
              type="button"
              class="ob-card block w-[138px]"
              :class="{ 'is-open': flipped }"
              :aria-label="flipped ? `Твоя карта: ${intent?.card.name}` : 'Открыть карту'"
              :aria-disabled="flipped || undefined"
              @click="onFlip"
            >
              <TarotCard
                ref="cardRef"
                class="w-full"
                :card-key="intent?.card.name ?? ''"
                :image-url="cardFace(intent?.card.deck ?? '19')"
                :back-image-url="cardBack"
                relative
              />
            </button>
            <span
              v-if="!flipped"
              class="ob-hint paper-tag type-overline pointer-events-none absolute -bottom-2.5 left-1/2 px-2 py-0.5"
              aria-hidden="true"
            >нажми</span>
            <span
              v-else
              class="ob-name paper-tag type-label pointer-events-none absolute -bottom-3 left-1/2 whitespace-nowrap px-2.5 py-0.5"
            >{{ intent?.card.roman }} · {{ intent?.card.name }}</span>
          </div>
          <div
            v-if="!flipped"
            class="mt-2 text-sm text-muted-foreground"
          >
            Карта для тебя готова — открой её
          </div>
          <div
            v-if="flipped"
            class="ob-reveal mt-3 w-full"
          >
            <div class="flex items-center gap-3">
              <span
                class="ob-avatar paper-tag grid h-11 w-11 shrink-0 place-items-center"
                aria-hidden="true"
              >
                <img
                  :src="catIcon"
                  alt=""
                  class="h-7 w-7 object-contain brightness-0 opacity-80"
                />
              </span>
              <div>
                <div class="font-serif text-base text-foreground">Таро Кот</div>
                <div class="text-[12px] text-muted-foreground">твоя карта: {{ intent?.card.name }}</div>
              </div>
            </div>
            <div class="ob-note paper-sheet mt-2 px-[18px] py-4 text-[15px] leading-[1.58] text-foreground">
              {{ intent?.reading }}
            </div>
          </div>
        </div>

        <div
          v-if="flipped"
          class="ob-reveal mt-[18px]"
        >
          <Button
            as-child
            size="lg"
            class="w-full justify-center"
          >
            <RouterLink :to="APP_CTA_TO">{{ APP_CTA_LABEL }} →</RouterLink>
          </Button>
          <div class="mt-3 flex items-center justify-between gap-3">
            <span class="max-w-[240px] text-[12.5px] leading-[1.4] text-muted-foreground">
              Дальше кот запомнит тебя и разложит карты полностью.
            </span>
            <button
              type="button"
              class="ob-link shrink-0 px-1 py-2 font-sans text-sm text-muted-foreground transition-colors hover:text-foreground"
              @click="reset"
            >
              спросить ещё
            </button>
          </div>
          <div class="mt-2 text-center">
            <a
              :href="TELEGRAM_BOT_URL"
              target="_blank"
              rel="noopener"
              class="text-[13px] text-primary-ink underline-offset-4 hover:underline"
            >или в Telegram</a>
          </div>
        </div>
        <div
          v-else
          class="mt-[18px] flex items-center gap-3"
        >
          <button
            type="button"
            class="ob-link px-1 py-2 font-sans text-sm text-muted-foreground transition-colors hover:text-foreground"
            @click="stage = 1"
          >
            ← назад
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* A strip of cream washi tape holding the demo sheet onto the sky (as PaperLetter). */
.ob-tape {
  position: absolute;
  top: -10px;
  left: 50%;
  width: 92px;
  height: 22px;
  transform: translateX(-50%) rotate(-2.5deg);
  background: color-mix(in srgb, var(--paper-stock) 82%, transparent);
  clip-path: var(--paper-cut-tape);
  box-shadow: 0 2px 4px var(--paper-shade);
}
.ob-step-tag {
  border-radius: var(--paper-radius-xs);
  transform: rotate(-1.5deg);
  box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.5), 0 3px 6px -2px var(--paper-shade);
}

/* Progress tabs (as FlowHeader's): hand-cut by uneven radii, not a clip-path. */
.ob-tab {
  height: 8px;
  border-radius: 3px 2px 3px 1.5px / 2px 3px 1.5px 2.5px;
  transition:
    translate 0.3s cubic-bezier(0.22, 1, 0.36, 1),
    height 0.3s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.3s;
}
.ob-tab.is-done {
  opacity: 0.7;
}
.ob-tab.is-now {
  height: 10px;
  translate: 0 -2px;
  box-shadow:
    inset 0 0 0 1px color-mix(in srgb, var(--paper-stock-ink) 30%, transparent),
    0 1px 0 rgb(4 3 18 / 0.55),
    0 3px 5px -2px var(--paper-shade);
}
.ob-tab.is-todo {
  background: var(--muted);
  box-shadow:
    inset 0 1px 2px var(--paper-shade),
    inset 0 0 0 1px color-mix(in srgb, var(--foreground) 15%, transparent);
}

/* Intent slips: a patch of the intent's own stock, a hair askew, an ink icon on a cream disc. */
.ob-slip {
  border-radius: var(--paper-radius-sm);
  transform: rotate(var(--tilt));
  box-shadow: inset 0 1px 0 var(--paper-edge), 0 6px 12px -6px var(--paper-shade);
  transition:
    transform 0.2s cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 0.2s ease-out;
}
.ob-slip:hover {
  transform: rotate(0deg) translateY(-2px);
  box-shadow: inset 0 1px 0 var(--paper-edge), 0 10px 16px -8px var(--paper-shade);
}
.ob-slip:active {
  transform: rotate(var(--tilt)) translateY(1px);
}
.ob-icon {
  background: var(--paper-stock);
  color: var(--paper-stock-ink);
  box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.5), 0 2px 4px -1px var(--paper-shade);
}
/* The chosen intent, as a small tag of its stock. */
.ob-kind {
  border-radius: var(--paper-radius-xs);
  transform: rotate(-1deg);
  box-shadow: inset 0 1px 0 var(--paper-edge), 0 3px 6px -2px var(--paper-shade);
}

/* The question well (as the Input primitive: recessed, pencil border, cream focus ring). */
.ob-well {
  border-radius: var(--paper-radius-sm);
  border: 1px solid color-mix(in srgb, var(--foreground) 40%, transparent);
  background: var(--muted);
  box-shadow: inset 0 2px 4px var(--paper-shade);
}
.ob-well:focus-within {
  outline: 3px solid color-mix(in srgb, var(--paper-stock) 50%, transparent);
  outline-offset: 1px;
}

/* Example questions: cream slips of paper. */
.ob-chip {
  border-radius: var(--paper-radius-xs);
  transform: rotate(var(--tilt));
  box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.5), 0 3px 6px -2px var(--paper-shade);
  transition: transform 0.15s ease-out;
}
.ob-chip:hover {
  transform: rotate(0deg) translateY(-1px);
}

.ob-slip:focus-visible,
.ob-chip:focus-visible,
.ob-card:focus-visible,
.ob-link:focus-visible {
  outline: 2px solid var(--paper-stock);
  outline-offset: 3px;
}

/* The card: a sticker, a little askew; straightens once turned. */
.ob-card {
  --sticker-w: 4px;
  transform: rotate(-2deg);
  transition: transform 0.4s cubic-bezier(0.22, 1, 0.36, 1);
}
.ob-card.is-open {
  transform: none;
  cursor: default;
}
.ob-card.is-open :deep(.card) {
  cursor: default;
}
.ob-hint,
.ob-name {
  border-radius: var(--paper-radius-xs);
  transform: translateX(-50%) rotate(-2deg);
  box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.5), 0 3px 6px -2px var(--paper-shade);
}
.ob-avatar {
  border-radius: 52% 48% 50% 46% / 48% 52% 46% 50%;
  box-shadow: 0 1px 0 rgb(4 3 18 / 0.5), 0 3px 6px -3px var(--paper-shade);
}
.ob-note {
  border-radius: var(--paper-radius-sm);
}

/* Stage entrance — slides in (transform + opacity only). */
.ob-step,
.ob-reveal {
  animation: ob-step-in 0.4s cubic-bezier(0.22, 1, 0.36, 1) both;
}
@keyframes ob-step-in {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
}

/* Loading dots shimmer. */
.ob-dots {
  animation: ob-dots 1.4s infinite;
}
@keyframes ob-dots {
  0% {
    opacity: 0.2;
  }
  20% {
    opacity: 1;
  }
  100% {
    opacity: 0.2;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ob-step,
  .ob-reveal,
  .ob-dots {
    animation: none;
  }
  .ob-tab,
  .ob-slip,
  .ob-chip,
  .ob-card {
    transition: none;
  }
}
</style>
